import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Phone, Clock, DollarSign, TrendingUp, Calendar, 
  Play, FileText, ArrowRight, Plus, CreditCard, 
  BarChart3, Headphones, Settings, ChevronRight, MessageSquare,
  Crown, Zap, Check, Sparkles, LogOut, CalendarCheck, Link2, Unlink, ExternalLink,
  PhoneForwarded, PhoneIncoming, Copy, CheckCircle2, HelpCircle, ArrowLeft,
  Mic, Star, Shield, Users, Globe, Code, Building2, Lock, Volume2, PieChart,
  MapPin, Trash2, Edit3, Receipt, RefreshCw
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { PlanChoiceCards } from '../components/PlanChoiceCards';
import { SmsSetupDialog } from '../components/sms/SmsSetupDialog';
import { SmsStatusCard } from '../components/sms/SmsStatusCard';
import { LocationAIDialog } from '../components/LocationAIDialog';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier-specific feature definitions
const TIER_FEATURES = {
  free: {
    color: 'gray',
    icon: Zap,
    maxMinutes: 10,
    features: ['Basic Front Desk Pro', '10 Included Minutes', 'Standard Voice'],
    locked: []
  },
  starter: {
    color: 'blue',
    icon: Zap,
    maxMinutes: 150,
    features: [
      'Pro 24/7',
      '150 Minutes Included',
      'Local Phone Number',
      'Call Recordings & Transcripts',
      'Custom Pro Personality',
      'Appointment Requests'
    ],
    locked: []
  },
  pro: {
    color: 'purple',
    icon: Crown,
    maxMinutes: 400,
    features: [
      'Pro 24/7',
      '400 Minutes Included',
      'Local Phone Number',
      'Call Analytics & Recordings',
      'Custom Pro Personality',
      'Calendar Integration',
      'Priority Support',
      'Multiple Phone Numbers',
      'API Access'
    ],
    locked: []
  }
};

const BusinessDashboard = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, user, token } = useAuth();
  const [business, setBusiness] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const pendingPlanRef = useRef(null);
  const [showSmsSetup, setShowSmsSetup] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [phoneSetupChoice, setPhoneSetupChoice] = useState(null); // 'forwarding' or 'new_number'
  const [forwardingNumber, setForwardingNumber] = useState('');
  const [forwardingDelay, setForwardingDelay] = useState('15'); // seconds before Pro picks up
  const [onboardingSaving, setOnboardingSaving] = useState(false);
  const [transferPhoneNumber, setTransferPhoneNumber] = useState(''); // Customer's phone for transfers
  
  // SMS Add-on state
  const [wantSmsFeature, setWantSmsFeature] = useState(null); // null, 'yes', 'no'
  const [showSmsAddonModal, setShowSmsAddonModal] = useState(false);
  const [smsCheckoutLoading, setSmsCheckoutLoading] = useState(false);
  
  // Locations state
  const [locations, setLocations] = useState([]);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [addingLocation, setAddingLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    area_code: '',
    transfer_phone_number: '',
    hours: ''
  });
  
  // Phone number location preferences
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [availableAreaCodes, setAvailableAreaCodes] = useState([]);
  const [loadingAreaCodes, setLoadingAreaCodes] = useState(false);
  
  // Phone number selection
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);

  // US States list
  const usStates = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
  ];

  // Fetch available area codes when state changes
  const fetchAreaCodes = async (state) => {
    if (!state) return;
    setLoadingAreaCodes(true);
    setAvailableNumbers([]); // Clear numbers when state changes
    setSelectedPhoneNumber(null);
    try {
      const res = await axios.get(`${API}/phone-numbers/area-codes?state=${state}`);
      setAvailableAreaCodes(res.data.area_codes || []);
      if (res.data.area_codes?.length > 0) {
        setAreaCode(res.data.area_codes[0].code);
        // Auto-search for numbers with first area code
        searchPhoneNumbers(res.data.area_codes[0].code);
      }
    } catch (err) {
      console.error('Failed to fetch area codes:', err);
      // Fallback to some common area codes
      setAvailableAreaCodes([]);
    } finally {
      setLoadingAreaCodes(false);
    }
  };

  // Search for available phone numbers
  const searchPhoneNumbers = async (searchAreaCode) => {
    if (!searchAreaCode) return;
    setLoadingNumbers(true);
    setSelectedPhoneNumber(null);
    try {
      const res = await axios.get(`${API}/phone-numbers/search?area_code=${searchAreaCode}&limit=8`);
      setAvailableNumbers(res.data.numbers || []);
    } catch (err) {
      console.error('Failed to search phone numbers:', err);
      setAvailableNumbers([]);
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Check for calendar connection callback
  useEffect(() => {
    const calendarConnected = searchParams.get('calendar_connected');
    const calendarError = searchParams.get('calendar_error');
    
    if (calendarConnected === 'true') {
      toast.success('Google Calendar connected successfully!');
      fetchCalendarStatus();
      // Clean URL
      navigate(`/business/${businessId}`, { replace: true });
    } else if (calendarError) {
      toast.error(`Calendar connection failed: ${calendarError}`);
      navigate(`/business/${businessId}`, { replace: true });
    }
  }, [searchParams]);

  const planParamHandled = useRef(false);
  useEffect(() => {
    const plan = searchParams.get('plan');
    if (!business || !plan || planParamHandled.current) return;
    planParamHandled.current = true;
    if (business.payment_confirmed && business.subscription_tier === plan) {
      toast.info(`You're already on the ${plan === 'pro' ? 'Pro' : 'Starter'} plan.`);
      return;
    }
    handleUpgrade(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, searchParams]);

  useEffect(() => {
    if (businessId) {
      // Reset all state when switching businesses to prevent stale data
      setBusiness(null);
      setSubscription(null);
      setUsage(null);
      setCalls([]);
      setLoading(true);
      setShowOnboarding(false);
      setLocations([]);
      fetchData();
      fetchCalendarStatus();
    }
  }, [businessId]);

  const fetchCalendarStatus = async () => {
    if (!token || !businessId) return;
    try {
      const res = await axios.get(`${API}/business/${businessId}/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch calendar status:', err);
    }
  };

  const handleConnectCalendar = async () => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }
    setCalendarLoading(true);
    try {
      const res = await axios.get(`${API}/business/${businessId}/calendar/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Google OAuth
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect calendar');
      setCalendarLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar from this business?')) return;
    
    setCalendarLoading(true);
    try {
      await axios.delete(`${API}/business/${businessId}/calendar/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Calendar disconnected');
      setCalendarStatus({ connected: false });
    } catch (err) {
      toast.error('Failed to disconnect calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [businessRes, subscriptionRes, usageRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}`),
        axios.get(`${API}/subscription/business/${businessId}`),
        axios.get(`${API}/subscription/usage/${businessId}`)
      ]);
      
      setBusiness(businessRes.data);
      setSubscription(subscriptionRes.data);
      setUsage(usageRes.data);
      
      // Check if onboarding is needed (no phone setup completed)
      if (!businessRes.data.onboarding_completed) {
        setShowOnboarding(true);
      }
      
      // Fetch recent calls for THIS business only (secure endpoint)
      try {
        const callsRes = await axios.get(`${API}/business/my-calls?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCalls(callsRes.data.calls || []);
      } catch (err) {
        console.log('Could not fetch calls');
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      
      // Handle specific error cases
      if (err.response?.status === 404) {
        toast.error('Business not found. Redirecting to dashboard...');
        // Redirect to main dashboard to pick a different business
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (err.response?.status === 401) {
        // Auth issue - let the interceptor handle it
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load dashboard. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch locations on mount
  useEffect(() => {
    if (businessId && token) {
      fetchLocations();
    }
  }, [businessId, token]);

  // Save onboarding preferences
  const handleSaveOnboarding = async () => {
    if (!phoneSetupChoice) {
      toast.error('Please select a phone setup option');
      return;
    }
    
    if (phoneSetupChoice === 'forwarding' && !forwardingNumber) {
      toast.error('Please enter your business phone number');
      return;
    }
    
    if (phoneSetupChoice === 'new_number' && !selectedState) {
      toast.error('Please select a state for your phone number');
      return;
    }
    
    if (!transferPhoneNumber) {
      toast.error('Please enter your phone number for call transfers');
      return;
    }
    
    // Check if token is available
    if (!token) {
      toast.error('Session expired. Please log in again.');
      navigate('/login');
      return;
    }
    
    setOnboardingSaving(true);
    try {
      const payload = {
        phone_setup_choice: phoneSetupChoice,
        forwarding_number: phoneSetupChoice === 'forwarding' ? forwardingNumber : null,
        forwarding_delay_seconds: phoneSetupChoice === 'forwarding' ? parseInt(forwardingDelay) : null,
        // Location preferences for new number
        preferred_state: phoneSetupChoice === 'new_number' ? selectedState : null,
        preferred_city: phoneSetupChoice === 'new_number' ? selectedCity : null,
        preferred_area_code: phoneSetupChoice === 'new_number' ? areaCode : null,
        // Specific phone number selected by user (white-label selection)
        selected_phone_number: phoneSetupChoice === 'new_number' && selectedPhoneNumber ? selectedPhoneNumber.phone_number : null,
        // Transfer phone number - where Pro transfers calls when customer asks for human
        transfer_phone_number: transferPhoneNumber
      };
      
      // Use axios with explicit auth header from token
      const authToken = token || localStorage.getItem('token');
      const res = await axios.post(`${API}/business/${businessId}/onboarding`, payload, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      setShowOnboarding(false);

      if (!business?.payment_confirmed) {
        const planId = pendingPlanRef.current;
        pendingPlanRef.current = null;
        if (!planId) {
          toast.success('Setup saved! Now pick your plan to activate your Pro.');
          setShowUpgradeModal(true);
          return;
        }
        toast.success(`Setup saved! Taking you to secure checkout for ${planId === 'pro' ? 'Pro' : 'Starter'}...`);
        await goToCheckout(planId);
        return;
      }

      if (res.data.phone_numbers?.retell_number) {
        toast.success(`Your Pro number is ready: ${res.data.phone_numbers.retell_number}`);
      } else {
        toast.success('Setup saved! Your Pro is updated.');
      }
      
      // Refresh business data
      const businessRes = await axios.get(`${API}/businesses/${businessId}`);
      setBusiness(businessRes.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save setup. Please try again.';
      toast.error(errorMsg);
    } finally {
      setOnboardingSaving(false);
    }
  };

  const handleBuyMinutes = async (minutes) => {
    try {
      const res = await axios.post(`${API}/subscription/minutes-checkout`, {
        business_id: businessId,
        minutes: minutes,
        origin_url: window.location.origin
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error('Failed to create checkout');
    }
  };

  // Texting add-on checkout ($25/month)
  const handlePurchaseSmsAddon = async () => {
    setSmsCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/sms-addon-checkout`, {
        business_id: businessId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to turn on texting');
    } finally {
      setSmsCheckoutLoading(false);
    }
  };

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/business/${businessId}/locations`);
      setLocations(res.data.locations || []);
    } catch (err) {
      console.log('Could not fetch locations:', err);
    }
  };

  // Add new location
  const handleAddLocation = async () => {
    if (!newLocation.name || !newLocation.address || !newLocation.city || !newLocation.state) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAddingLocation(true);
    try {
      const res = await axios.post(`${API}/business/${businessId}/locations`, newLocation);
      toast.success(`Location "${newLocation.name}" added! Phone number: ${res.data.location?.phone_number || 'Pending'}`);
      setShowAddLocationModal(false);
      setNewLocation({ name: '', address: '', city: '', state: '', area_code: '', transfer_phone_number: '', hours: '' });
      fetchLocations();
    } catch (err) {
      console.error('Add location error:', err);
      toast.error(err.response?.data?.detail || 'Failed to add location');
    } finally {
      setAddingLocation(false);
    }
  };

  // Delete location
  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location? This will release the phone number.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/business/${businessId}/locations/${locationId}`);
      toast.success('Location deleted');
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete location');
    }
  };

  // Upgrade to a paid plan - show onboarding if not completed
  const handleUpgrade = async (planId) => {
    setShowUpgradeModal(false);
    // If onboarding wasn't completed, show onboarding modal first, then checkout
    if (!business?.onboarding_completed) {
      pendingPlanRef.current = planId;
      setOnboardingStep(1);
      setShowOnboarding(true);
      return;
    }
    await goToCheckout(planId);
  };

  const openBillingPortal = async () => {
    try {
      const res = await axios.post(`${API}/business/${businessId}/billing-portal`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open billing');
    }
  };

  const goToCheckout = async (planId) => {
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        business_id: businessId,
        plan_id: planId,
        billing_type: 'recurring',
        payment_method: 'stripe',
        origin_url: window.location.origin
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      toast.error(err.response?.data?.detail || 'Failed to start upgrade process');
    }
  };

  // Demo account plan switching (for testing)
  const handleSwitchPlan = async (planId) => {
    try {
      const res = await axios.post(`${API}/demo/switch-plan`, {
        business_id: businessId,
        plan_id: planId
      });
      
      if (res.data.success) {
        toast.success(`Switched to ${planId} plan!`);
        fetchData(); // Refresh dashboard
      }
    } catch (err) {
      toast.error('Failed to switch plan');
    }
  };

  // Check if this is a demo account
  const isDemoAccount = business?.owner_user_id === 'demo-user-001' || business?.business_name === 'Demo Auto Repair';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-modern p-8 text-center">
          <h1 className="text-xl font-bold text-foreground mb-2">Business Not Found</h1>
          <p className="text-muted-foreground mb-4">The business you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  const minutesRemaining = usage?.minutes_balance || 0;
  const minutesUsed = usage?.minutes_used || 0;
  const currentCost = usage?.current_usage_cost || 0;
  const monthlyFee = usage?.monthly_fee || subscription?.monthly_price || 0;
  const smsUsed = usage?.sms_used || 0;
  const smsBalance = usage?.sms_balance || 0;
  const smsIncluded = usage?.sms_included || 100;
  
  // Minutes tracking calculations
  const includedMinutes = usage?.minutes_included || 10;
  const planName = usage?.plan_name || 'Trial';
  const locationPrice = usage?.additional_location_price ?? 2;
  const totalMinutesUsed = minutesUsed;
  const minutesPercentUsed = Math.min(100, (totalMinutesUsed / includedMinutes) * 100);
  const isInOverage = totalMinutesUsed > includedMinutes;
  const overageMinutes = isInOverage ? totalMinutesUsed - includedMinutes : 0;
  const overageCost = overageMinutes * 0.25;

  return (
    <div className="min-h-screen bg-background p-4 lg:p-6">
      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ touchAction: 'manipulation' }}>
          <Card className="card-modern w-full max-w-2xl max-h-[90vh] overflow-y-auto relative" data-testid="onboarding-modal" style={{ touchAction: 'auto' }}>
            {/* Close button */}
            <button
              onClick={() => setShowOnboarding(false)}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors z-10"
              data-testid="onboarding-close-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            {/* Step 1: Welcome */}
            {onboardingStep === 1 && (
              <div className="p-6 sm:p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Welcome to Front Desk Pro! 🎉
                  </h2>
                  <p className="text-muted-foreground">
                    Let's get your Pro set up in just 2 minutes.
                  </p>
                </div>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">Account Created</h3>
                      <p className="text-sm text-muted-foreground">Your {business.business_name} account is ready</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">Next: Phone Setup</h3>
                      <p className="text-sm text-muted-foreground">Choose how customers will reach your Pro</p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full h-12 bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600"
                  onClick={() => setOnboardingStep(2)}
                  data-testid="onboarding-start-btn"
                >
                  Let's Get Started <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Phone Setup - Direct to New Pro Number */}
            {onboardingStep === 2 && (
              <div className="p-6 sm:p-8">
                <button 
                  onClick={() => setOnboardingStep(1)}
                  className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </button>
                
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                    <PhoneIncoming className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Get Your Pro Phone Number
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Choose a local number for your Pro
                  </p>
                </div>
                
                <div className="bg-muted/50 rounded-xl p-5 mb-6">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                    What you'll get:
                  </h3>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Dedicated local phone number
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Pro answers calls instantly 24/7
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Transfers to you when needed
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Perfect for marketing & ads
                    </li>
                  </ul>
                </div>
                
                <Button 
                  className="w-full h-12 bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600"
                  onClick={() => {
                    setPhoneSetupChoice('new_number');
                    setOnboardingStep(3);
                  }}
                  data-testid="onboarding-next-step2"
                >
                  Choose My Number <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <div className="mt-6 pt-6 border-t border-border" data-testid="onboarding-forwarding-section">
                  {phoneSetupChoice !== 'forwarding' ? (
                    <button
                      type="button"
                      onClick={() => setPhoneSetupChoice('forwarding')}
                      className="w-full text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
                      data-testid="onboarding-choose-forwarding"
                    >
                      Prefer to keep your current business number? Use call forwarding instead
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Keep your existing number</p>
                      <p className="text-xs text-muted-foreground">We give your Pro its own line, and you forward your business number to it when you can't answer.</p>
                      <input
                        type="tel"
                        value={forwardingNumber}
                        onChange={(e) => setForwardingNumber(e.target.value)}
                        placeholder="Your business phone, e.g. (937) 555-0123"
                        className="w-full h-11 px-3 rounded-lg border border-border bg-background text-foreground"
                        data-testid="onboarding-forwarding-number"
                      />
                      <input
                        type="tel"
                        value={transferPhoneNumber}
                        onChange={(e) => setTransferPhoneNumber(e.target.value)}
                        placeholder="Your cell for live transfers"
                        className="w-full h-11 px-3 rounded-lg border border-border bg-background text-foreground"
                        data-testid="onboarding-forwarding-transfer"
                      />
                      <select
                        value={forwardingDelay}
                        onChange={(e) => setForwardingDelay(e.target.value)}
                        className="w-full h-11 px-3 rounded-lg border border-border bg-background text-foreground"
                        data-testid="onboarding-forwarding-delay"
                      >
                        <option value="0">Forward immediately</option>
                        <option value="15">After 15 seconds (about 3 rings)</option>
                        <option value="25">After 25 seconds (about 5 rings)</option>
                      </select>
                      <Button
                        className="w-full h-11"
                        onClick={handleSaveOnboarding}
                        disabled={onboardingSaving}
                        data-testid="onboarding-forwarding-save"
                      >
                        {onboardingSaving ? 'Saving...' : 'Finish Setup with Call Forwarding'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Phone Number Selection */}
            {onboardingStep === 3 && (
              <div className="p-6 sm:p-8">
                <button 
                  onClick={() => setOnboardingStep(2)}
                  className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </button>
                
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Choose Your Phone Number Location
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Select a state and area code for your new Pro phone number
                  </p>
                </div>
                    
                    <div className="space-y-5 mb-6">
                      {/* State Selection */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          State
                        </label>
                        <select
                          value={selectedState}
                          onChange={(e) => {
                            setSelectedState(e.target.value);
                            setAreaCode('');
                            fetchAreaCodes(e.target.value);
                          }}
                          className="w-full h-12 px-3 rounded-lg bg-background border border-border text-foreground cursor-pointer text-base"
                          data-testid="state-select"
                        >
                          <option value="">Select a state...</option>
                          {usStates.map((state) => (
                            <option key={state.code} value={state.code}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* City (Optional) */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          City <span className="text-muted-foreground">(optional)</span>
                        </label>
                        <Input
                          type="text"
                          placeholder="e.g., Houston, Dallas, Austin"
                          value={selectedCity}
                          onChange={(e) => setSelectedCity(e.target.value)}
                          className="h-12"
                          data-testid="city-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          We'll try to get a number from this area
                        </p>
                      </div>
                      
                      {/* Area Code Selection */}
                      {selectedState && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Preferred Area Code
                          </label>
                          {loadingAreaCodes ? (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                              Loading area codes...
                            </div>
                          ) : availableAreaCodes.length > 0 ? (
                            <div className="grid grid-cols-4 gap-2" data-testid="area-codes-grid">
                              {availableAreaCodes.slice(0, 8).map((ac) => (
                                <button
                                  key={ac.code}
                                  onClick={() => {
                                    setAreaCode(ac.code);
                                    searchPhoneNumbers(ac.code);
                                  }}
                                  className={`p-3 rounded-lg border text-center transition-all ${
                                    areaCode === ac.code
                                      ? 'border-cyan-500 bg-cyan-500/10'
                                      : 'border-border hover:border-cyan-500/50'
                                  }`}
                                  data-testid={`area-code-${ac.code}`}
                                >
                                  <p className="font-bold text-foreground">{ac.code}</p>
                                  <p className="text-xs text-muted-foreground truncate">{ac.city || 'Available'}</p>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <Input
                              type="text"
                              placeholder="Enter area code (e.g., 713)"
                              value={areaCode}
                              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                              className="h-12"
                              maxLength={3}
                            />
                          )}
                        </div>
                      )}
                      
                      {/* Available Phone Numbers Selection */}
                      {areaCode && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Choose Your Phone Number
                          </label>
                          {loadingNumbers ? (
                            <div className="flex items-center gap-2 text-muted-foreground py-4">
                              <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                              Searching available numbers...
                            </div>
                          ) : availableNumbers.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2" data-testid="phone-numbers-grid">
                              {availableNumbers.map((num, index) => (
                                <button
                                  key={num.phone_number}
                                  onClick={() => setSelectedPhoneNumber(num)}
                                  className={`p-3 rounded-lg border text-left transition-all ${
                                    selectedPhoneNumber?.phone_number === num.phone_number
                                      ? 'border-green-500 bg-green-500/10 ring-2 ring-green-500/50'
                                      : 'border-border hover:border-green-500/50'
                                  }`}
                                  data-testid={`phone-number-card-${index}`}
                                >
                                  <p className="font-bold text-foreground text-lg">{num.formatted}</p>
                                  {num.city && (
                                    <p className="text-xs text-muted-foreground">{num.city}, {num.state}</p>
                                  )}
                                  {selectedPhoneNumber?.phone_number === num.phone_number && (
                                    <div className="flex items-center gap-1 mt-1 text-green-500 text-xs">
                                      <CheckCircle2 className="w-3 h-3" />
                                      Selected
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              <p>No numbers available for this area code.</p>
                              <p className="text-sm">Try a different area code.</p>
                            </div>
                          )}
                          
                          {availableNumbers.length > 0 && (
                            <button
                              onClick={() => searchPhoneNumbers(areaCode)}
                              className="mt-3 text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Show different numbers
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* What you'll get */}
                      <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border border-cyan-500/30">
                        <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-cyan-400" />
                          What You'll Get
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-foreground">Local {selectedState || 'area'} phone number for Pro calls</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-foreground">Separate text message number (same area)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-foreground">Both numbers auto-linked to your account</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                        <p className="text-sm text-foreground">
                          <strong>Note:</strong> Phone numbers will be purchased automatically after payment is confirmed. You'll receive both numbers via email.
                        </p>
                      </div>
                    </div>
                
                {/* Transfer Phone Number */}
                <div className="mt-6 p-5 rounded-xl bg-orange-500/10 border border-orange-500/30">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-orange-400" />
                    Your Personal Phone Number (Required)
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    When a caller asks to speak to a real person, your Pro will transfer the call to this number.
                  </p>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={transferPhoneNumber}
                    onChange={(e) => setTransferPhoneNumber(e.target.value)}
                    className="h-12"
                    data-testid="transfer-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    This is YOUR phone number - not your business line. Your Pro will ring this when someone requests a human.
                  </p>
                </div>
                
                <Button 
                  className="w-full h-12 mt-6 bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600"
                  onClick={() => setOnboardingStep(4)}
                  disabled={!transferPhoneNumber || !selectedState}
                  data-testid="onboarding-next-step3"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 4: SMS Feature Question */}
            {onboardingStep === 4 && (
              <div className="p-6 sm:p-8">
                <button 
                  onClick={() => setOnboardingStep(3)}
                  className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </button>
                
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Would You Like Text Messaging?
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Send appointment reminders, confirmations and owner alerts by text, from your own business texting number
                  </p>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Yes Option */}
                  <button
                    onClick={() => setWantSmsFeature('yes')}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                      wantSmsFeature === 'yes'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-border hover:border-cyan-500/50'
                    }`}
                    data-testid="sms-choice-yes"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        wantSmsFeature === 'yes' ? 'bg-cyan-500' : 'bg-muted'
                      }`}>
                        <Check className={`w-6 h-6 ${
                          wantSmsFeature === 'yes' ? 'text-white' : 'text-muted-foreground'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">Yes, Add Text Messaging</h3>
                          <span className="text-xs bg-cyan-500/20 text-cyan-700 px-2 py-0.5 rounded-full normal-case">$25/mo, add after checkout</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Get a dedicated phone number for automated text messages to customers and yourself.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-500" /> Appointment reminders to customers
                          </li>
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-500" /> Booking confirmations
                          </li>
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-500" /> Owner notifications for new bookings
                          </li>
                          <li className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-500" /> Just Texting add-on $25/mo
                          </li>
                        </ul>
                      </div>
                    </div>
                  </button>
                  
                  {/* No Option */}
                  <button
                    onClick={() => setWantSmsFeature('no')}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                      wantSmsFeature === 'no'
                        ? 'border-gray-500 bg-gray-500/10'
                        : 'border-border hover:border-gray-500/50'
                    }`}
                    data-testid="sms-choice-no"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        wantSmsFeature === 'no' ? 'bg-gray-500' : 'bg-muted'
                      }`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={wantSmsFeature === 'no' ? 'text-white' : 'text-muted-foreground'}>
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1">No Thanks, Skip for Now</h3>
                        <p className="text-sm text-muted-foreground">
                          You can always add text messaging later from your dashboard.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
                
                {wantSmsFeature === 'yes' ? (
                  <Button 
                    className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                    onClick={async () => {
                      await handleSaveOnboarding();
                    }}
                    disabled={onboardingSaving || smsCheckoutLoading}
                    data-testid="onboarding-complete-with-sms"
                  >
                    {(onboardingSaving || smsCheckoutLoading) ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Complete Setup & Continue <CreditCard className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                ) : (
                  <Button 
                    className="w-full h-12 bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600"
                    onClick={handleSaveOnboarding}
                    disabled={onboardingSaving || !wantSmsFeature}
                    data-testid="onboarding-complete-btn"
                  >
                    {onboardingSaving ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {phoneSetupChoice === 'new_number' ? 'Purchasing Numbers...' : 'Saving...'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Complete Setup <CheckCircle2 className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
              {business.business_name}
            </h1>
            <p className="text-muted-foreground text-sm mt-1 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                subscription?.subscription_status === 'active' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {subscription?.subscription_tier === 'pro' ? 'PRO' : (!subscription?.subscription_tier || subscription?.subscription_tier === 'free') ? 'TRIAL' : subscription?.subscription_tier?.toUpperCase()}
              </span>
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/agent-settings`)}>
              <Settings className="w-4 h-4 mr-2" /> My Pro
            </Button>
            <Button onClick={() => handleBuyMinutes(100)} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> Buy Minutes
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              data-testid="dashboard-logout-btn"
              className="text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-500/30"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        
        {isDemoAccount && (
          <Card className="card-modern p-4 mb-6 border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-3">
              <span className="text-amber-500 text-xl">🧪</span>
              <div>
                <h3 className="font-medium text-foreground">Demo Mode</h3>
                <p className="text-sm text-muted-foreground">This is a demo account for testing the platform</p>
              </div>
            </div>
          </Card>
        )}

        {/* Get Started Banner - Show when onboarding not completed */}
        {!business?.onboarding_completed && !showOnboarding && (
          <Card className="card-modern p-6 mb-6 border-violet-500/30 bg-gradient-to-r from-violet-500/10 to-cyan-500/10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">Complete Your Setup</h3>
                  <p className="text-muted-foreground text-sm">
                    Get your Pro ready to answer calls in just 2 minutes. We'll help you set up your phone number and customize your Pro.
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => {
                  setOnboardingStep(1);
                  setShowOnboarding(true);
                }}
                className="bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 whitespace-nowrap"
                data-testid="get-started-btn"
              >
                <Zap className="w-4 h-4 mr-2" /> Get Started
              </Button>
            </div>
          </Card>
        )}

        {/* Minutes Tracker - Prominent Usage Display */}
        <Card className="card-modern p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Left side - Main tracker */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Minutes Tracker
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isInOverage 
                    ? 'bg-red-500/20 text-red-400' 
                    : minutesRemaining <= 50 
                      ? 'bg-yellow-500/20 text-yellow-400' 
                      : 'bg-green-500/20 text-green-400'
                }`}>
                  {!business?.payment_confirmed && !['starter', 'pro'].includes(business?.subscription_tier) ? 'TRIAL' : isInOverage ? 'IN OVERAGE' : minutesRemaining <= 50 ? 'LOW BALANCE' : 'HEALTHY'}
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="relative h-4 bg-muted rounded-full overflow-hidden mb-3">
                <div 
                  className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${
                    isInOverage 
                      ? 'bg-red-500' 
                      : minutesPercentUsed > 80 
                        ? 'bg-yellow-500' 
                        : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, minutesPercentUsed)}%` }}
                />
                {isInOverage && (
                  <div 
                    className="absolute right-0 top-0 h-full bg-red-600 animate-pulse"
                    style={{ width: '100%' }}
                  />
                )}
              </div>
              
              {/* Stats row */}
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-muted-foreground">Used: </span>
                  <span className="font-semibold text-foreground">{totalMinutesUsed} mins</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Included: </span>
                  <span className="font-semibold text-foreground">{includedMinutes} mins</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Remaining: </span>
                  <span className={`font-semibold ${isInOverage ? 'text-red-400' : 'text-green-400'}`}>
                    {isInOverage ? '0' : minutesRemaining} mins
                  </span>
                </div>
              </div>
              
              {/* Overage warning */}
              {isInOverage && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm font-medium">
                    ⚠️ You've used {overageMinutes} overage minutes (${overageCost.toFixed(2)} at $0.25/min)
                  </p>
                </div>
              )}
              
              {['past_due', 'cancelled'].includes(business?.subscription_status) && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="billing-problem-notice">
                  <p className="text-red-700 text-sm font-medium">
                    {business.subscription_status === 'past_due'
                      ? "We couldn't charge your card for this month. Update it under Manage Billing to keep your Pro answering."
                      : 'Your plan was cancelled. Choose a plan to turn your Pro back on.'}
                  </p>
                </div>
              )}
              {!business?.payment_confirmed && !['starter', 'pro'].includes(business?.subscription_tier) && (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="trial-minutes-notice">
                  <p className="text-blue-600 text-sm font-medium">
                    You're on the free trial ({minutesRemaining} minutes). Choose Starter or Pro to get 150 or 400 minutes.
                  </p>
                </div>
              )}
              {business?.payment_confirmed && minutesRemaining > 0 && minutesRemaining <= 50 && (
                <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-600 text-sm font-medium">
                    Low balance! Only {minutesRemaining} minutes remaining. Overage is $0.25/min.
                  </p>
                </div>
              )}
            </div>
            
            {/* Right side - Quick stats */}
            <div className="flex gap-4 lg:gap-6">
              <div className="text-center p-4 bg-muted/30 rounded-lg min-w-[100px]">
                <Phone className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{usage?.total_calls || 0}</p>
                <p className="text-xs text-muted-foreground">Total Calls</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg min-w-[100px]">
                <MessageSquare className="w-6 h-6 text-teal-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{smsUsed}</p>
                <p className="text-xs text-muted-foreground">Texts Sent</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg min-w-[100px]">
                <DollarSign className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">${(currentCost + overageCost).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Current Charges</p>
              </div>
            </div>
          </div>
          
          {/* Buy more minutes button */}
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Plan: <span className="font-medium text-foreground">{includedMinutes} mins/month</span> • 
              Overage: <span className="font-medium text-foreground">$0.25/min</span> • 
              Texting: <span className="font-medium text-foreground">$25/mo add-on</span>
            </p>
            <Button onClick={() => handleBuyMinutes(100)} size="sm" className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> Buy More Minutes
            </Button>
          </div>
        </Card>

        {/* Secondary Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Monthly Subscription</p>
                <p className="text-2xl font-bold text-foreground">${monthlyFee}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-modern p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Locations</p>
                <p className="text-2xl font-bold text-foreground">{locations.length || 1}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Google Calendar Integration Card */}
        <Card className="card-modern p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                calendarStatus?.connected 
                  ? 'bg-green-500/20' 
                  : 'bg-blue-500/20'
              }`}>
                {calendarStatus?.connected ? (
                  <CalendarCheck className="w-6 h-6 text-green-400" />
                ) : (
                  <Calendar className="w-6 h-6 text-blue-400" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  Google Calendar
                  {calendarStatus?.connected && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                      Connected
                    </span>
                  )}
                </h3>
                {calendarStatus?.connected ? (
                  <p className="text-sm text-muted-foreground">
                    Synced with <span className="text-foreground">{calendarStatus.email}</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Connect to let Pro book appointments directly to your calendar
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto">
              {calendarStatus?.connected ? (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/calendar')}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    View Calendar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDisconnectCalendar}
                    disabled={calendarLoading}
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-500/30"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={handleConnectCalendar}
                  disabled={calendarLoading}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="connect-calendar-btn"
                >
                  {calendarLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Connect Google Calendar
                </Button>
              )}
            </div>
          </div>
          
          {!calendarStatus?.connected && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Pro books appointments automatically</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Checks your availability in real-time</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Sends calendar invites to customers</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* SMS Text Messaging Feature Card */}
        <SmsStatusCard businessId={businessId} />
        <SmsSetupDialog open={showSmsSetup} onClose={() => setShowSmsSetup(false)} business={business} onCheckout={handlePurchaseSmsAddon} />

        {!business?.sms_feature_enabled && !business?.sms_addon_paid && (
          <Card className="card-modern p-6 mb-6 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-indigo-500/10 border-cyan-500/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <MessageSquare className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    Turn On Text Messaging
                    <span className="text-sm bg-cyan-500/20 text-cyan-700 px-2.5 py-0.5 rounded-full normal-case tracking-normal font-medium" data-testid="sms-addon-price">$25/month</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Send appointment reminders, confirmations and owner alerts by text, from your own business texting number
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => business?.payment_confirmed ? setShowSmsSetup(true) : toast.error("Please choose a plan first, then add texting")}
                disabled={smsCheckoutLoading}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                data-testid="purchase-sms-btn"
              >
                {smsCheckoutLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                ) : (
                  <MessageSquare className="w-4 h-4 mr-2" />
                )}
                Turn On Texting ($25/mo)
              </Button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-cyan-500/20">
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Appointment reminders to customers</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Booking confirmations</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Owner notifications</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>Texting add-on $25/mo</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* SMS Feature Enabled Card - Show when active */}
        {business?.sms_feature_enabled && business?.sms_phone_number && (
          <Card className="card-modern p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground flex items-center gap-2">
                    Text Messaging
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    SMS Number: <span className="text-foreground font-mono">{business.sms_phone_number}</span>
                  </p>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/sms-settings')}
              >
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </div>
          </Card>
        )}

        {/* Billing & Payments Section */}
        <Card className="card-modern p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Billing & Payments</h3>
                <p className="text-xs text-muted-foreground">
                  View your subscription and payment history
                </p>
              </div>
            </div>
          </div>

          {/* Current Plan Summary */}
          <div className="bg-muted/30 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Current Plan</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                subscription?.subscription_status === 'active' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {subscription?.subscription_status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Plan</p>
                <p className="font-semibold text-foreground">
                  {planName}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Monthly Fee</p>
                <p className="font-semibold text-foreground">${monthlyFee || 0}/mo</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Included Minutes</p>
                <p className="font-semibold text-foreground">{includedMinutes} mins</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Current Usage</p>
                <p className="font-semibold text-foreground">${(currentCost + overageCost).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {business?.stripe_customer_id && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-border" data-testid="auto-renew-row">
              <p className="text-sm text-muted-foreground">
                {business?.auto_renew === false ? 'Your plan will not renew.' : 'Auto-pay is on. Your plan renews monthly and minutes refill each renewal.'}
              </p>
              <Button size="sm" variant="outline" onClick={openBillingPortal} data-testid="manage-billing-btn">Manage Billing</Button>
            </div>
          )}

          {/* Recent Payments */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Recent Payments</h4>
            {usage?.recent_transactions?.some(t => t.payment_status === 'paid') ? (
              <div className="space-y-2">
                {usage.recent_transactions.filter(t => t.payment_status === 'paid').slice(0, 5).map((txn, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        txn.payment_status === 'paid' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                      }`}>
                        {txn.payment_status === 'paid' ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {txn.type === 'subscription' ? 'Subscription' : 
                           txn.type === 'minutes' ? 'Minutes Purchase' : 
                           txn.type === 'sms_addon' ? 'SMS Add-on (old)' : 
                           txn.type === 'setup_fee' ? 'Setup Fee' : 'Payment'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${txn.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                        ${Number(txn.amount || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize">{txn.payment_status}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No payments yet</p>
              </div>
            )}
          </div>
        </Card>

        {/* Locations Section */}
        <Card className="card-modern p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Locations</h3>
                <p className="text-xs text-muted-foreground">
                  {locations.length} location{locations.length !== 1 ? 's' : ''} • Minutes shared across all
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowAddLocationModal(true)}
              size="sm"
              className="bg-gradient-to-r from-violet-500 to-purple-500"
              data-testid="add-location-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Location
            </Button>
          </div>

          <div className="space-y-3">
            {locations.map((location) => (
              <div 
                key={location.id}
                className={`p-4 rounded-xl border ${location.is_primary ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/30'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-foreground">{location.name}</h4>
                      {location.is_primary && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {location.address}{location.city && `, ${location.city}`}{location.state && `, ${location.state}`}
                    </p>
                    {location.phone_number && (
                      <p className="text-sm text-foreground font-mono mt-1">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {location.phone_number}
                      </p>
                    )}
                    {location.hours && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {location.hours}
                      </p>
                    )}
                  </div>
                  {!location.is_primary && (
                    <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingLocation(location)}
                      data-testid={`location-edit-ai-${location.id}`}
                    >
                      <Settings className="w-4 h-4 mr-1" /> Edit Pro
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLocation(location.id)}
                      className="text-red-400 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    </div>
                  )}
                  {location.is_primary && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/agent-settings')} data-testid="location-edit-ai-primary">
                      <Settings className="w-4 h-4 mr-1" /> Edit Pro
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              <strong>${locationPrice}/month</strong> per additional location • No extra signup fee • Each gets its own phone number • Minutes shared
            </p>
          </div>
        </Card>

        {/* Add Location Modal */}
        {editingLocation && <LocationAIDialog businessId={businessId} location={editingLocation} onClose={() => setEditingLocation(null)} onSaved={fetchLocations} />}
        {showAddLocationModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md p-6 bg-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">Add New Location</h2>
                <button 
                  onClick={() => setShowAddLocationModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Location Name *</label>
                  <Input
                    placeholder="e.g., Downtown Office, Westside Branch"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                    data-testid="location-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Street Address *</label>
                  <Input
                    value={newLocation.address}
                    onChange={(e) => setNewLocation({...newLocation, address: e.target.value})}
                    placeholder="123 Main Street"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">City *</label>
                    <Input
                      placeholder="City"
                      value={newLocation.city}
                      onChange={(e) => setNewLocation({...newLocation, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">State *</label>
                    <select
                      value={newLocation.state}
                      onChange={(e) => setNewLocation({...newLocation, state: e.target.value})}
                      className="w-full h-10 rounded-md border border-border bg-muted px-3 text-sm text-foreground"
                    >
                      <option value="">Select State</option>
                      {usStates.map(state => (
                        <option key={state.code} value={state.code}>{state.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Area Code (Optional)</label>
                  <Input
                    placeholder="e.g., 937"
                    value={newLocation.area_code}
                    onChange={(e) => setNewLocation({...newLocation, area_code: e.target.value})}
                    maxLength={3}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Preferred area code for the phone number</p>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Transfer Calls To (Optional)</label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={newLocation.transfer_phone_number}
                    onChange={(e) => setNewLocation({...newLocation, transfer_phone_number: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Where to transfer calls when customer requests a human</p>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Business Hours (Optional)</label>
                  <Input
                    placeholder="Mon-Fri 9am-5pm"
                    value={newLocation.hours}
                    onChange={(e) => setNewLocation({...newLocation, hours: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowAddLocationModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddLocation}
                  disabled={addingLocation}
                  className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500"
                  data-testid="confirm-add-location"
                >
                  {addingLocation ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add Location (+${locationPrice}/mo)
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Upgrade Plan Banner - Show for free tier */}
        {(subscription?.subscription_tier === 'free' || !subscription?.subscription_tier) && subscription?.subscription_tier !== 'pro' && (
          <Card className="card-modern p-6 mb-6 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 border-purple-500/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    Upgrade Your Plan
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Starter 150 min or Pro 400 min included • $0.25/min overage • texting add-on $25/mo
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button 
                  onClick={() => setShowUpgradeModal(true)}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid="upgrade-banner-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Choose a Plan
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowUpgradeModal(false)}>
            <Card className="bg-card border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-border">
                <h2 className="text-2xl font-bold text-foreground">Choose Your Plan</h2>
                <p className="text-muted-foreground">Get everything you need for your business</p>
              </div>
              
              <div className="p-6">
                <PlanChoiceCards currentTier={subscription?.subscription_tier} onChoose={handleUpgrade} />
              </div>
              
              <div className="p-6 border-t border-border text-center">
                <p className="text-sm text-muted-foreground">
                  Cancel anytime. No long-term contracts.
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Calls */}
          <div className="lg:col-span-2">
            <Card className="card-modern">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary" />
                  Recent Calls
                </h2>
                <Button variant="ghost" size="sm" onClick={() => navigate('/calls')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <div className="divide-y divide-border">
                {calls.length === 0 ? (
                  <div className="p-8 text-center">
                    <Headphones className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No calls yet</p>
                    <p className="text-sm text-muted-foreground">Calls will appear here once your Pro starts taking them</p>
                  </div>
                ) : (
                  calls.slice(0, 5).map((call) => (
                    <div key={call.call_id} className="p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-foreground font-medium">
                            {call.from_number || 'Unknown'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {call.duration_ms ? `${Math.round(call.duration_ms / 1000 / 60)} min` : 'N/A'} •{' '}
                            {call.start_timestamp ? new Date(call.start_timestamp).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          call.call_status === 'ended' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {call.call_status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="card-modern p-4">
              <h3 className="font-semibold text-foreground mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/calendar')}
                >
                  <Calendar className="w-4 h-4 mr-2" /> View Calendar
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/calls')}
                >
                  <FileText className="w-4 h-4 mr-2" /> Call Transcripts
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/voice')}
                >
                  <Play className="w-4 h-4 mr-2" /> Test Front Desk Pro
                </Button>
              </div>
            </Card>

            {/* Buy Minutes */}
            <Card className="card-modern p-4">
              <h3 className="font-semibold text-foreground mb-4">Buy Minutes</h3>
              <p className="text-sm text-muted-foreground mb-4">
                $0.25 per minute • Never miss a call
              </p>
              <div className="space-y-2">
                {[
                  { mins: 100, price: 25 },
                  { mins: 500, price: 125 },
                  { mins: 1000, price: 250 }
                ].map((pkg) => (
                  <Button
                    key={pkg.mins}
                    data-testid={`buy-${pkg.mins}-mins`}
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => handleBuyMinutes(pkg.mins)}
                  >
                    <span>{pkg.mins} minutes</span>
                    <span className="text-primary font-semibold">${pkg.price}</span>
                  </Button>
                ))}
              </div>
            </Card>

            {/* Phone Number */}
            <Card className="card-modern p-4">
              <h3 className="font-semibold text-foreground mb-2">Your Pro Phone Number</h3>
              {business.retell_phone_number ? (
                <p className="text-2xl font-mono text-primary">{business.retell_phone_number}</p>
              ) : business.onboarding_completed === false ? (
                <div>
                  <p className="text-muted-foreground text-sm mb-3">
                    Complete setup to get your dedicated phone number
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowOnboarding(true)}
                  >
                    Complete Setup
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-muted-foreground text-sm mb-3">
                    Your phone number will be ready shortly...
                  </p>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary w-2/3 animate-pulse" />
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Tier-Specific Features Section */}
        {(() => {
          const tier = subscription?.subscription_tier || 'free';
          const tierInfo = TIER_FEATURES[tier] || TIER_FEATURES.free;
          const TierIcon = tierInfo.icon;
          
          return (
            <div className="mt-8">
              {/* Current Plan Features */}
              <Card className="card-modern p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-${tierInfo.color}-500/20 flex items-center justify-center`}>
                    <TierIcon className={`w-6 h-6 text-${tierInfo.color}-500`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground capitalize">
                      {tier === 'free' ? 'Trial' : tier === 'pro' ? 'Pro' : tier} Plan Features
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Your plan includes
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tierInfo.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {tierInfo.locked.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-3">Upgrade to unlock:</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {tierInfo.locked.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                          <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      className="mt-4"
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      <Zap className="w-4 h-4 mr-2" /> Upgrade Plan
                    </Button>
                  </div>
                )}
              </Card>

              {/* Analytics Section - Professional & Enterprise only */}
              {(tier === 'professional' || tier === 'enterprise') && (
                <Card className="card-modern p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-purple-500" />
                      Call Analytics
                    </h3>
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                      {tier === 'enterprise' ? 'Advanced' : 'Detailed'}
                    </span>
                  </div>
                  
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold text-foreground">{calls.length}</p>
                      <p className="text-sm text-muted-foreground">Total Calls</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold text-foreground">
                        {calls.length > 0 ? Math.round(calls.reduce((sum, c) => sum + (c.duration_ms || 0), 0) / 60000) : 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Minutes</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold text-foreground">
                        {calls.filter(c => c.call_status === 'ended').length}
                      </p>
                      <p className="text-sm text-muted-foreground">Completed Calls</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold text-foreground">
                        {calls.length > 0 ? `${Math.round(calls.reduce((sum, c) => sum + (c.duration_ms || 0), 0) / calls.length / 1000)}s` : '0s'}
                      </p>
                      <p className="text-sm text-muted-foreground">Avg Duration</p>
                    </div>
                  </div>
                  
                  {tier === 'enterprise' && (
                    <div className="mt-4 p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Code className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-medium text-foreground">API Access</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Access your call data programmatically via our REST API
                      </p>
                      <code className="text-xs bg-muted px-2 py-1 rounded text-foreground">
                        GET /api/business/my-calls
                      </code>
                    </div>
                  )}
                </Card>
              )}

              {/* Call Recording - Professional & Enterprise */}
              {(tier === 'professional' || tier === 'enterprise') && calls.length > 0 && (
                <Card className="card-modern p-6 mb-6">
                  <h3 className="text-lg font-bold text-foreground flex items-center gap-2 mb-4">
                    <Volume2 className="w-5 h-5 text-cyan-500" />
                    Call Recordings
                  </h3>
                  <div className="space-y-3">
                    {calls.slice(0, 3).map((call) => (
                      <div key={call.call_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                            <Phone className="w-5 h-5 text-cyan-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{call.from_number || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">
                              {call.start_timestamp ? new Date(call.start_timestamp).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/calls/${call.call_id}`)}
                        >
                          <Play className="w-3 h-3 mr-1" /> Play
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Multi-Number Management - Enterprise only */}
              {tier === 'enterprise' && (
                <Card className="card-modern p-6 mb-6">
                  <h3 className="text-lg font-bold text-foreground flex items-center gap-2 mb-4">
                    <Globe className="w-5 h-5 text-orange-500" />
                    Multiple Phone Numbers
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enterprise plans include up to 5 dedicated phone numbers for different departments or locations.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">Main Line</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {business.retell_phone_number || 'Not configured'}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Active</span>
                    </div>
                    <Button variant="outline" className="w-full">
                      <Plus className="w-4 h-4 mr-2" /> Add Phone Number
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default BusinessDashboard;
